/**
 * Tab de Configuración de Cámaras (solo admin)
 * Permite agregar, editar y eliminar cámaras
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Slider,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { DiscoverySortableHeader, useDiscoveryColumnSort } from '../shared/DiscoverySortableHeader';
import api from '../../services/api';
import { useOrientation } from '../../hooks/useOrientation';
import { useTranslation } from 'react-i18next';
import { getUser, isDemo } from '../../services/auth';
import { getDemoActivityTracker } from '../../services/demoActivityTracker';
import { getDemoRestrictionSnackbarProps } from '../../constants/demoRestrictions';

type CameraType = 'dahua' | 'grandstream';

interface Camera {
  id: number;
  name: string;
  camera_ip: string;
  camera_port: number;
  camera_username: string;
  camera_password?: string;
  habitacion_id: number | null;
  enabled: boolean;
  camera_type?: CameraType;
  motion_sensitivity?: number;
}

interface Habitacion {
  id: number;
  name: string; // El backend devuelve 'name', no 'nombre'
}

interface CameraConfigTabProps {
  isDemoUser?: boolean;
  onCamerasChanged?: () => void;
}

const CameraConfigTab: React.FC<CameraConfigTabProps> = ({ isDemoUser = false, onCamerasChanged }) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'error' });
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [habitaciones, setHabitaciones] = useState<Habitacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cameraToDelete, setCameraToDelete] = useState<Camera | null>(null);
  const [editingCamera, setEditingCamera] = useState<Camera | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const savedSensitivity = useRef<Record<number, number>>({});
  const [formData, setFormData] = useState({
    name: '',
    camera_ip: '',
    camera_port: 554,
    camera_username: 'admin',
    camera_password: '',
    habitacion_id: '',
    enabled: true,
    camera_type: 'dahua' as CameraType,
    motion_sensitivity: 3,
  });

  useEffect(() => {
    // Cargar habitaciones primero para evitar conflictos
    loadHabitaciones().then(() => {
    loadCameras();
    });
  }, []);

  const sensitivityLevel = (camera: Camera) => (
    camera.motion_sensitivity && camera.motion_sensitivity >= 1 && camera.motion_sensitivity <= 5
      ? camera.motion_sensitivity
      : 3
  );

  type CameraSortKey = 'name' | 'type' | 'ip' | 'port' | 'room' | 'status' | 'sensitivity';

  const cameraColumns: { key: CameraSortKey; label: string; sx: object }[] = [
    { key: 'name', label: t('videoStreaming.cameraConfig.tableHeaders.name'), sx: {} },
    { key: 'type', label: t('videoStreaming.cameraConfig.tableHeaders.type'), sx: {} },
    { key: 'ip', label: t('videoStreaming.cameraConfig.tableHeaders.ip'), sx: {} },
    { key: 'port', label: t('videoStreaming.cameraConfig.tableHeaders.port'), sx: {} },
    { key: 'room', label: t('videoStreaming.cameraConfig.tableHeaders.room'), sx: {} },
    { key: 'status', label: t('videoStreaming.cameraConfig.tableHeaders.status'), sx: {} },
    { key: 'sensitivity', label: t('videoStreaming.cameraConfig.tableHeaders.sensitivity'), sx: {} },
  ];

  const getCameraSortValue = useCallback((camera: Camera, key: CameraSortKey) => {
    if (key === 'name') return camera.name || '';
    if (key === 'type') {
      return camera.camera_type === 'grandstream'
        ? t('videoStreaming.cameraConfig.cameraTypes.grandstream')
        : t('videoStreaming.cameraConfig.cameraTypes.dahua');
    }
    if (key === 'ip') return camera.camera_ip || '';
    if (key === 'port') return String(camera.camera_port ?? '');
    if (key === 'room') return habitaciones.find((h) => h.id === camera.habitacion_id)?.name || '';
    if (key === 'status') {
      return camera.enabled
        ? t('videoStreaming.cameraConfig.status.enabled')
        : t('videoStreaming.cameraConfig.status.disabled');
    }
    return String(sensitivityLevel(camera));
  }, [t, habitaciones]);

  const {
    sorted: sortedCameras,
    sortKey: camSortKey,
    sortDir: camSortDir,
    toggleSort: toggleCamSort,
  } = useDiscoveryColumnSort(cameras, getCameraSortValue);

  const cameraHead = (key: CameraSortKey, label: string, extra?: object) => (
    <TableCell onClick={() => toggleCamSort(key)} sx={{ cursor: 'pointer', ...extra }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.35 }}>
        {camSortKey === key && (
          camSortDir === 'asc'
            ? <ArrowUpward sx={{ fontSize: 12 }} />
            : <ArrowDownward sx={{ fontSize: 12 }} />
        )}
        {label}
      </Box>
    </TableCell>
  );

  const loadCameras = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get('/video/cameras');
      if (response.data.success) {
        const list = response.data.cameras as Camera[];
        setCameras(list);
        const saved: Record<number, number> = {};
        list.forEach((c) => { saved[c.id] = sensitivityLevel(c); });
        savedSensitivity.current = saved;
      }
    } catch (error) {
      console.error('Error cargando cámaras:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadHabitaciones = async () => {
    try {
      const response = await api.get('/video/rooms');
      // Verificar que response.data sea un array
      if (Array.isArray(response.data)) {
        setHabitaciones(response.data);
      } else {
        console.error('Error: response.data no es un array:', response.data);
        setHabitaciones([]); // Establecer array vacío para evitar errores
      }
    } catch (error: any) {
      console.error('Error cargando habitaciones:', error);
      // En caso de error, establecer array vacío para evitar errores de map
      setHabitaciones([]);
    }
  };

  const notifyDemoRestriction = () => {
    setSnackbar({
      open: true,
      message: t('permissions.demoRestriction'),
      severity: 'error'
    });
  };

  const handleOpenDialog = (camera?: Camera) => {
    if (isDemoUser) {
      const user = getUser();
      if (user && isDemo(user)) {
        const tracker = getDemoActivityTracker();
        tracker.trackClick(
          camera ? 'edit_camera_button' : 'add_camera_button',
          camera?.id || null,
          camera?.name || 'Nueva cámara',
          {
            screen: 'video',
            tab: 'configuration',
            attemptedAction: camera ? 'edit_camera' : 'add_camera',
            blocked: true,
            blockReason: 'demo_user_no_configure'
          }
        );
      }
      notifyDemoRestriction();
      return;
    }
    if (camera) {
      setEditingCamera(camera);
      setFormData({
        name: camera.name,
        camera_ip: camera.camera_ip,
        camera_port: camera.camera_port,
        camera_username: camera.camera_username || 'admin',
        camera_password: '', // No mostrar password existente
        habitacion_id: camera.habitacion_id?.toString() || '',
        enabled: camera.enabled,
        camera_type: camera.camera_type === 'grandstream' ? 'grandstream' : 'dahua',
        motion_sensitivity: camera.motion_sensitivity && camera.motion_sensitivity >= 1 && camera.motion_sensitivity <= 5
          ? camera.motion_sensitivity
          : 3,
      });
    } else {
      setEditingCamera(null);
      setFormData({
        name: '',
        camera_ip: '',
        camera_port: 554,
        camera_username: 'admin',
        camera_password: '',
        habitacion_id: '',
        enabled: true,
        camera_type: 'dahua',
        motion_sensitivity: 3,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCamera(null);
  };

  const handleSave = async () => {
    if (isDemoUser) {
      const user = getUser();
      if (user && isDemo(user)) {
        const tracker = getDemoActivityTracker();
        tracker.trackClick(
          'save_camera_button',
          editingCamera?.id || null,
          editingCamera?.name || formData.name || 'Nueva cámara',
          {
            screen: 'video',
            tab: 'configuration',
            attemptedAction: editingCamera ? 'save_camera_edit' : 'save_camera_new',
            blocked: true,
            blockReason: 'demo_user_no_configure'
          }
        );
      }
      notifyDemoRestriction();
      return;
    }
    setSaving(true);
    try {
      const data: Record<string, unknown> = {
        ...formData,
        camera_port: parseInt(formData.camera_port.toString()),
        habitacion_id: formData.habitacion_id ? parseInt(formData.habitacion_id) : null,
      };
      if (editingCamera && !formData.camera_password) {
        delete data.camera_password;
      }

      const requestOptions = { timeout: 25000 };
      if (editingCamera) {
        await api.put(`/video/cameras/${editingCamera.id}`, data, requestOptions);
      } else {
        await api.post('/video/cameras', data, requestOptions);
      }

      handleCloseDialog();
      loadCameras();
      onCamerasChanged?.();
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        (error.code === 'ECONNABORTED'
          ? t('videoStreaming.cameraConfig.dialog.errors.timeout')
          : t('videoStreaming.cameraConfig.dialog.errors.saving'));
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (isDemoUser) {
      notifyDemoRestriction();
      return;
    }
    if (!formData.camera_ip || !formData.camera_username || !formData.camera_password) {
      setSnackbar({
        open: true,
        message: t('videoStreaming.cameraConfig.dialog.testNeedFields'),
        severity: 'error',
      });
      return;
    }
    setTesting(true);
    try {
      const response = await api.post(
        '/video/cameras/test-connection',
        {
          camera_ip: formData.camera_ip,
          camera_port: parseInt(formData.camera_port.toString()) || 554,
          camera_username: formData.camera_username,
          camera_password: formData.camera_password,
          camera_type: formData.camera_type,
        },
        { timeout: 35000 },
      );
      const ok = Boolean(response.data?.success);
      setSnackbar({
        open: true,
        message: response.data?.message || (ok
          ? t('videoStreaming.cameraConfig.dialog.testOk')
          : t('videoStreaming.cameraConfig.dialog.errors.testFailed')),
        severity: ok ? 'success' : 'error',
      });
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (error.code === 'ECONNABORTED'
          ? t('videoStreaming.cameraConfig.dialog.errors.timeout')
          : t('videoStreaming.cameraConfig.dialog.errors.testFailed'));
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async (cameraId: number) => {
    if (isDemoUser) {
      const user = getUser();
      if (user && isDemo(user)) {
        const tracker = getDemoActivityTracker();
        const camera = cameras.find((c) => c.id === cameraId);
        tracker.trackClick(
          'delete_camera_button',
          cameraId,
          camera?.name || `Cámara ${cameraId}`,
          {
            screen: 'video',
            tab: 'configuration',
            attemptedAction: 'delete_camera',
            blocked: true,
            blockReason: 'demo_user_no_configure'
          }
        );
      }
      notifyDemoRestriction();
      return;
    }
    const camera = cameras.find((c) => c.id === cameraId);
    if (camera) {
      setCameraToDelete(camera);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (isDemoUser) {
      const user = getUser();
      if (user && isDemo(user)) {
        const tracker = getDemoActivityTracker();
        tracker.trackClick(
          'confirm_delete_camera_button',
          cameraToDelete?.id || null,
          cameraToDelete?.name || 'Cámara',
          {
            screen: 'video',
            tab: 'configuration',
            attemptedAction: 'confirm_delete_camera',
            blocked: true,
            blockReason: 'demo_user_no_configure'
          }
        );
      }
      notifyDemoRestriction();
      setDeleteDialogOpen(false);
      setCameraToDelete(null);
      return;
    }
    if (!cameraToDelete) return;

    try {
      const response = await api.delete(`/video/cameras/${cameraToDelete.id}`);
      
      if (response.status === 200 || response.data?.success) {
        setDeleteDialogOpen(false);
        setCameraToDelete(null);
        loadCameras();
        onCamerasChanged?.();
      } else {
        alert(response.data?.error || 'Error eliminando cámara');
      }
    } catch (error: unknown) {
      void error;
    }
  };

  const handleToggleEnabled = async (camera: Camera) => {
    if (isDemoUser) {
      const user = getUser();
      if (user && isDemo(user)) {
        const tracker = getDemoActivityTracker();
        tracker.trackClick(
          'toggle_camera_enabled',
          camera.id,
          camera.name,
          {
            screen: 'video',
            tab: 'configuration',
            attemptedAction: `toggle_camera_${camera.enabled ? 'disable' : 'enable'}`,
            blocked: true,
            blockReason: 'demo_user_no_configure',
            value: { enabled: !camera.enabled }
          }
        );
      }
      notifyDemoRestriction();
      return;
    }
    const newEnabled = !camera.enabled;
    
    // Actualización optimista: cambiar el estado inmediatamente en la UI
    setCameras(prevCameras => 
      prevCameras.map(c => 
        c.id === camera.id ? { ...c, enabled: newEnabled } : c
      )
    );
    
    try {
      const response = await api.put(`/video/cameras/${camera.id}`, {
        enabled: newEnabled,
      });
      
      if (response.data.success) {
        // Recargar para asegurar sincronización con el backend
        loadCameras();
        onCamerasChanged?.();
      } else {
        // Revertir cambio si falla
        setCameras(prevCameras => 
          prevCameras.map(c => 
            c.id === camera.id ? { ...c, enabled: !newEnabled } : c
          )
        );
        alert(response.data.error || 'Error actualizando cámara');
      }
    } catch (error: unknown) {
      // Revertir cambio si falla
      setCameras(prevCameras => 
        prevCameras.map(c => 
          c.id === camera.id ? { ...c, enabled: !newEnabled } : c
        )
      );
      void error;
    }
  };

  const handleSensitivity = async (camera: Camera, level: number) => {
    const previous = savedSensitivity.current[camera.id] ?? sensitivityLevel(camera);
    if (isDemoUser) {
      notifyDemoRestriction();
      setCameras((prev) => prev.map((c) => (
        c.id === camera.id ? { ...c, motion_sensitivity: previous } : c
      )));
      return;
    }
    if (previous === level) return;
    setCameras((prev) => prev.map((c) => (
      c.id === camera.id ? { ...c, motion_sensitivity: level } : c
    )));
    try {
      const response = await api.put(`/video/cameras/${camera.id}`, {
        motion_sensitivity: level,
      });
      if (response.data.success) {
        savedSensitivity.current[camera.id] = level;
        loadCameras(true);
        onCamerasChanged?.();
      } else {
        setCameras((prev) => prev.map((c) => (
          c.id === camera.id ? { ...c, motion_sensitivity: previous } : c
        )));
        setSnackbar({
          open: true,
          message: response.data?.error || t('videoStreaming.cameraConfig.dialog.errors.updating'),
          severity: 'error',
        });
      }
    } catch (error: unknown) {
      setCameras((prev) => prev.map((c) => (
        c.id === camera.id ? { ...c, motion_sensitivity: previous } : c
      )));
      void error;
      setSnackbar({
        open: true,
        message: t('videoStreaming.cameraConfig.dialog.errors.updating'),
        severity: 'error',
      });
    }
  };

  const sensitivityControl = (camera: Camera, wide: boolean) => {
    const level = sensitivityLevel(camera);
    return (
      <Box sx={{ width: wide ? '100%' : 148, minWidth: wide ? 0 : 148, px: wide ? 0 : 0.5 }}>
        <Slider
          size="small"
          min={1}
          max={5}
          step={1}
          marks
          value={level}
          onChange={(_, value) => {
            const next = Array.isArray(value) ? value[0] : value;
            setCameras((prev) => prev.map((c) => (
              c.id === camera.id ? { ...c, motion_sensitivity: next } : c
            )));
          }}
          onChangeCommitted={(_, value) => {
            const next = Array.isArray(value) ? value[0] : value;
            void handleSensitivity(camera, next);
          }}
          sx={{
            color: '#2391FF',
            py: wide ? 0.5 : 0,
            padding: wide ? undefined : '4px 0',
            '& .MuiSlider-mark': { backgroundColor: '#888' },
            '& .MuiSlider-markActive': { backgroundColor: '#2391FF' },
          }}
        />
        <Typography sx={{ color: '#2391FF', fontSize: 11, lineHeight: 1.1, mt: -0.5 }}>
          {t(`videoStreaming.cameraConfig.dialog.motionLevels.${level}`)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#000',
      color: '#fff',
      boxSizing: 'border-box',
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
        paddingX: 1.5,
        paddingTop: '5px',
        paddingBottom: 0.5,
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}>
        <Typography variant="h6" sx={{ color: '#2391FF', fontWeight: 'bold', fontSize: '1rem' }}>
          {t('videoStreaming.cameraConfig.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#2391FF',
            '&:hover': { backgroundColor: '#1a7ad9' },
            marginRight: 0,
            paddingX: 1.5,
            paddingY: 0.5,
            minHeight: '32px',
            height: '32px',
            fontSize: '0.875rem',
          }}
        >
          {t('videoStreaming.cameraConfig.add')}
        </Button>
      </Box>

      <Box sx={{
        paddingX: 1.5,
        paddingTop: 0,
        paddingBottom: 1,
        width: '100%',
        boxSizing: 'border-box',
      }}>
          {loading ? (
          <Typography sx={{ color: '#888', textAlign: 'center' }}>{t('videoStreaming.cameraConfig.loading')}</Typography>
        ) : cameras.length === 0 ? (
          <Typography sx={{ color: '#888', textAlign: 'center' }}>
            {t('videoStreaming.cameraConfig.noCamerasConfigured')}
          </Typography>
        ) : (
          <>
              {/* Tabla responsive - En desktop se muestra como tabla */}
              {isHorizontal ? (
              <TableContainer 
                sx={{ 
                  backgroundColor: '#1A1A1A', 
                  borderRadius: 1, 
                  width: '100%', 
                  minWidth: '100%',
                  overflowX: 'auto', // Solo scroll horizontal si es necesario
                  display: 'block',
                  marginBottom: 2 // Espacio inferior para separar del final
                }}
              >
                <Table sx={{
                  width: '100%',
                  '& .MuiTableCell-root': {
                    fontSize: '0.78rem',
                    lineHeight: 1.35,
                    color: '#ccc',
                    borderColor: '#2a2a2a',
                    py: 0.4,
                    px: 1,
                  },
                  '& .MuiTableCell-head': {
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                    lineHeight: 1.2,
                    color: '#888',
                    borderColor: '#333',
                    py: 0.7,
                    whiteSpace: 'nowrap',
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      {cameraHead('name', t('videoStreaming.cameraConfig.tableHeaders.name'))}
                      {cameraHead('type', t('videoStreaming.cameraConfig.tableHeaders.type'))}
                      {cameraHead('ip', t('videoStreaming.cameraConfig.tableHeaders.ip'))}
                      {cameraHead('port', t('videoStreaming.cameraConfig.tableHeaders.port'))}
                      {cameraHead('room', t('videoStreaming.cameraConfig.tableHeaders.room'))}
                      {cameraHead('status', t('videoStreaming.cameraConfig.tableHeaders.status'))}
                      {cameraHead('sensitivity', t('videoStreaming.cameraConfig.tableHeaders.sensitivity'), { minWidth: 160 })}
                      <TableCell>{t('videoStreaming.cameraConfig.tableHeaders.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCameras.length > 0 ? (
                      sortedCameras.map((camera) => {
                        const habitacion = habitaciones.find((h) => h.id === camera.habitacion_id);
                        return (
                          <TableRow key={camera.id} sx={{ '&:hover': { backgroundColor: '#1F1F1F' } }}>
                            <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: '0.84rem' }}>{camera.name}</TableCell>
                            <TableCell>
                              {camera.camera_type === 'grandstream'
                                ? t('videoStreaming.cameraConfig.cameraTypes.grandstream')
                                : t('videoStreaming.cameraConfig.cameraTypes.dahua')}
                            </TableCell>
                            <TableCell>
                              <a
                                href={`http://${camera.camera_ip}`}
                                onClick={(e) => {
                                  // Usar window.open (igual que el icono de dispositivo) para que
                                  // en la PWA instalada se abra dentro de Safari y no en el navegador
                                  // por defecto del sistema (p. ej. Chrome).
                                  e.preventDefault();
                                  window.open(`http://${camera.camera_ip}`, '_blank', 'width=1600,height=1200,left=100,top=100');
                                }}
                                rel="noopener noreferrer"
                                title={`Abrir cámara http://${camera.camera_ip}`}
                                style={{ color: '#2391FF', textDecoration: 'underline' }}
                              >
                                {camera.camera_ip}
                              </a>
                            </TableCell>
                            <TableCell>{camera.camera_port}</TableCell>
                            <TableCell>{habitacion?.name || '-'}</TableCell>
                            <TableCell>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={camera.enabled}
                                    onChange={() => handleToggleEnabled(camera)}
                                    size="small"
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#2391FF',
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#2391FF',
                                      },
                                    }}
                                  />
                                }
                                label={camera.enabled ? t('videoStreaming.cameraConfig.status.enabled') : t('videoStreaming.cameraConfig.status.disabled')}
                                sx={{ color: '#ccc', m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'middle' }}>
                              {sensitivityControl(camera, false)}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => handleOpenDialog(camera)}
                                sx={{ color: '#2391FF', p: 0.5 }}
                                size="small"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDelete(camera.id)}
                                sx={{ color: '#ff4444', p: 0.5 }}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ color: '#888', textAlign: 'center', borderColor: '#333' }}>
                          {t('videoStreaming.cameraConfig.noCamerasConfigured')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              ) : null}

              {/* Vista móvil - Cards */}
              {!isHorizontal ? (
              <>
              {cameras.length > 0 && (
                <DiscoverySortableHeader
                  chips
                  columns={cameraColumns}
                  sortKey={camSortKey}
                  sortDir={camSortDir}
                  onToggle={toggleCamSort}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {sortedCameras.map((camera) => {
            const habitacion = habitaciones.find((h) => h.id === camera.habitacion_id);
            return (
              <Box
                key={camera.id}
                sx={{
                  backgroundColor: '#2a2d31',
                  borderRadius: '10px',
                  padding: 1.25,
                  border: '1px solid #3a3d42',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, pb: 0.7, borderBottom: '1px solid #3a3d42' }}>
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>
                    {camera.name}
                  </Typography>
                  <Box>
                    <IconButton
                      onClick={() => handleOpenDialog(camera)}
                      sx={{ color: '#2391FF' }}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(camera.id)}
                      sx={{ color: '#ff4444' }}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ color: '#8d8d8d', fontSize: '0.62rem', fontWeight: 600 }}>{t('videoStreaming.cameraConfig.mobileLabels.type')}</Typography>
                    <Typography sx={{ color: '#ccc', fontSize: '0.78rem' }}>
                      {camera.camera_type === 'grandstream'
                        ? t('videoStreaming.cameraConfig.cameraTypes.grandstream')
                        : t('videoStreaming.cameraConfig.cameraTypes.dahua')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ color: '#8d8d8d', fontSize: '0.62rem', fontWeight: 600 }}>{t('videoStreaming.cameraConfig.mobileLabels.ip')}</Typography>
                    <a
                      href={`http://${camera.camera_ip}`}
                      onClick={(e) => {
                        // Usar window.open (igual que el icono de dispositivo) para que
                        // en la PWA instalada se abra dentro de Safari y no en el navegador
                        // por defecto del sistema (p. ej. Chrome).
                        e.preventDefault();
                        window.open(`http://${camera.camera_ip}`, '_blank', 'width=1600,height=1200,left=100,top=100');
                      }}
                      rel="noopener noreferrer"
                      title={`Abrir cámara http://${camera.camera_ip}`}
                      style={{ color: '#2391FF', fontSize: '0.78rem', textDecoration: 'underline' }}
                    >
                      {camera.camera_ip}
                    </a>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#8d8d8d', fontSize: '0.62rem', fontWeight: 600 }}>{t('videoStreaming.cameraConfig.mobileLabels.port')}</Typography>
                    <Typography sx={{ color: '#ccc', fontSize: '0.78rem' }}>{camera.camera_port}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ color: '#8d8d8d', fontSize: '0.62rem', fontWeight: 600 }}>{t('videoStreaming.cameraConfig.mobileLabels.room')}</Typography>
                    <Typography sx={{ color: '#ccc', fontSize: '0.78rem' }}>{habitacion?.name || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#8d8d8d', fontSize: '0.62rem', fontWeight: 600 }}>{t('videoStreaming.cameraConfig.mobileLabels.status')}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={camera.enabled}
                          onChange={() => handleToggleEnabled(camera)}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#2391FF',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#2391FF',
                            },
                          }}
                        />
                      }
                      label={camera.enabled ? t('videoStreaming.cameraConfig.status.enabled') : t('videoStreaming.cameraConfig.status.disabled')}
                      sx={{ color: '#ccc', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem' } }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#8d8d8d', fontSize: '0.62rem', fontWeight: 600, mb: 0.25 }}>
                      {t('videoStreaming.cameraConfig.mobileLabels.sensitivity')}
                    </Typography>
                    {sensitivityControl(camera, true)}
                  </Box>
                </Box>
              </Box>
            );
                })}
              </Box>
              </>
              ) : null}
          </>
        )}
      </Box>

      {/* Dialog de edición/creación */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: '#1E1E1E',
            color: 'white',
            minWidth: '300px',
          },
        }}
      >
        <DialogTitle sx={{ 
          color: '#2391FF', 
          fontSize: '1rem',
          fontWeight: 'bold',
          paddingBottom: 1
        }}>
          {editingCamera ? t('videoStreaming.cameraConfig.dialog.editTitle') : t('videoStreaming.cameraConfig.dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('videoStreaming.cameraConfig.dialog.fields.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                },
                '& .MuiInputLabel-root': { color: '#888' },
              }}
            />
            <FormControl>
              <InputLabel sx={{ color: '#888' }}>{t('videoStreaming.cameraConfig.dialog.fields.cameraType')}</InputLabel>
              <Select
                value={formData.camera_type}
                label={t('videoStreaming.cameraConfig.dialog.fields.cameraType')}
                onChange={(e) =>
                  setFormData({ ...formData, camera_type: e.target.value as CameraType })
                }
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                  '& .MuiSvgIcon-root': { color: '#888' },
                }}
              >
                <MenuItem value="dahua">{t('videoStreaming.cameraConfig.cameraTypes.dahua')}</MenuItem>
                <MenuItem value="grandstream">{t('videoStreaming.cameraConfig.cameraTypes.grandstream')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('videoStreaming.cameraConfig.dialog.fields.cameraIp')}
              value={formData.camera_ip}
              onChange={(e) => setFormData({ ...formData, camera_ip: e.target.value })}
              required
              placeholder="10.1.101.207"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                },
                '& .MuiInputLabel-root': { color: '#888' },
              }}
            />
            <TextField
              label={t('videoStreaming.cameraConfig.dialog.fields.rtspPort')}
              type="number"
              value={formData.camera_port}
              onChange={(e) =>
                setFormData({ ...formData, camera_port: parseInt(e.target.value) || 554 })
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                },
                '& .MuiInputLabel-root': { color: '#888' },
              }}
            />
            <TextField
              label={t('videoStreaming.cameraConfig.dialog.fields.username')}
              value={formData.camera_username}
              onChange={(e) => setFormData({ ...formData, camera_username: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                },
                '& .MuiInputLabel-root': { color: '#888' },
              }}
            />
            <TextField
              label={t('videoStreaming.cameraConfig.dialog.fields.password')}
              type="password"
              value={formData.camera_password}
              onChange={(e) => setFormData({ ...formData, camera_password: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                },
                '& .MuiInputLabel-root': { color: '#888' },
              }}
            />
            <FormControl>
              <InputLabel sx={{ color: '#888' }}>{t('videoStreaming.cameraConfig.dialog.fields.room')}</InputLabel>
              <Select
                value={formData.habitacion_id}
                onChange={(e) => setFormData({ ...formData, habitacion_id: e.target.value })}
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
                  '& .MuiSvgIcon-root': { color: '#888' },
                }}
              >
                <MenuItem value="">
                  <em>{t('videoStreaming.cameraConfig.dialog.fields.roomNone')}</em>
                </MenuItem>
                {habitaciones.map((hab) => (
                  <MenuItem key={hab.id} value={hab.id.toString()}>
                    {hab.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#2391FF' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#2391FF',
                    },
                  }}
                />
              }
              label={t('videoStreaming.cameraConfig.dialog.fields.enabled')}
              sx={{ color: '#fff' }}
            />
            {!isDemoUser && (
              <Box>
                <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>
                  {t('videoStreaming.cameraConfig.dialog.fields.motionSensitivity')}
                </Typography>
                <Slider
                  min={1}
                  max={5}
                  step={1}
                  marks
                  value={formData.motion_sensitivity}
                  onChange={(_, value) => {
                    const next = Array.isArray(value) ? value[0] : value;
                    setFormData({ ...formData, motion_sensitivity: next });
                  }}
                  sx={{ color: '#2391FF', mt: 1 }}
                />
                <Typography sx={{ color: '#2391FF', fontSize: '0.85rem' }}>
                  {t(`videoStreaming.cameraConfig.dialog.motionLevels.${formData.motion_sensitivity}`)}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '0.75rem', mt: 0.5 }}>
                  {t('videoStreaming.cameraConfig.dialog.motionSensitivityHelp')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
          <Button
            onClick={handleTestConnection}
            disabled={saving || testing}
            startIcon={testing ? <CircularProgress size={16} sx={{ color: '#2391FF' }} /> : undefined}
            sx={{
              color: '#2391FF',
              border: '1px solid #2391FF',
              '&:hover': {
                backgroundColor: 'rgba(35, 145, 255, 0.12)',
              },
            }}
          >
            {testing
              ? t('videoStreaming.cameraConfig.dialog.testingConnection')
              : t('videoStreaming.cameraConfig.dialog.testConnection')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            disabled={saving || testing}
            sx={{ 
              color: '#888',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || testing}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
            sx={{
              backgroundColor: '#2391FF',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1a7ad9',
              },
            }}
          >
            {saving ? t('videoStreaming.cameraConfig.dialog.saving') : t('common.save')}
          </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCameraToDelete(null);
        }}
        PaperProps={{
          style: {
            backgroundColor: '#1E1E1E',
            color: 'white',
            minWidth: '300px',
          },
        }}
      >
        <DialogTitle sx={{ 
          color: '#2391FF', 
          fontSize: '1rem',
          fontWeight: 'bold',
          paddingBottom: 1
        }}>
          {t('settings.rooms.confirmDeletion', 'Confirmar eliminación')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
            {t('videoStreaming.cameraConfig.dialog.deleteConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setCameraToDelete(null);
            }}
            sx={{
              color: '#888',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{
              backgroundColor: '#f44336',
              color: 'white',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
            }}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar para restricciones de demo */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        {...getDemoRestrictionSnackbarProps()}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CameraConfigTab;
